<script setup lang="ts">
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Slideover } from "@/components/Base/Headless";
import Lucide from "@/components/Base/Lucide";
import api from "@/utils/axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import "dayjs/locale/gl";

dayjs.extend(relativeTime);

type NewsCard = {
  slug: string;
  title_gl: string;
  title_es: string;
  excerpt_gl?: string | null;
  excerpt_es?: string | null;
  cover_url?: string | null;
  published_at?: string | null;
};

interface MainProps {
  activitiesPanel: boolean;
  setActivitiesPanel: (val: boolean) => void;
}

const props = withDefaults(defineProps<MainProps>(), {
  activitiesPanel: false,
  setActivitiesPanel: () => {},
});

const { t, locale } = useI18n();
const router = useRouter();
const items = ref<NewsCard[]>([]);
const loading = ref(false);

const titleOf = (n: NewsCard) =>
  locale.value === "es" ? n.title_es || n.title_gl : n.title_gl || n.title_es;

const excerptOf = (n: NewsCard) => {
  const raw =
    locale.value === "es"
      ? n.excerpt_es || n.excerpt_gl
      : n.excerpt_gl || n.excerpt_es;
  return (raw || "").trim();
};

const formatWhen = (value: string | null | undefined) => {
  if (!value) return "";
  try {
    dayjs.locale(locale.value === "es" ? "es" : "gl");
    return dayjs(value).fromNow();
  } catch {
    return String(value).slice(0, 10);
  }
};

const fetchNews = async () => {
  loading.value = true;
  try {
    const response = await api.get("/public/news", { params: { limit: 15 } });
    items.value = response.data?.data?.items || [];
  } catch {
    items.value = [];
  } finally {
    loading.value = false;
  }
};

const openItem = (n: NewsCard) => {
  props.setActivitiesPanel(false);
  router.push({ name: "public-news-detail", params: { slug: n.slug } });
};

const openAll = () => {
  props.setActivitiesPanel(false);
  router.push({ name: "public-news" });
};

watch(
  () => props.activitiesPanel,
  (open) => {
    if (open) void fetchNews();
  }
);
</script>

<template>
  <div>
    <Slideover
      :open="props.activitiesPanel"
      @close="() => props.setActivitiesPanel(false)"
    >
      <Slideover.Panel class="w-80 sm:w-96 rounded-[0.75rem_0_0_0.75rem]">
        <a
          href=""
          class="focus:outline-none hover:bg-white/10 bg-white/5 transition-all hover:rotate-180 absolute inset-y-0 left-0 right-auto flex items-center justify-center my-auto -ml-[60px] sm:-ml-[105px] border rounded-full text-white/90 w-8 h-8 sm:w-14 sm:h-14 border-white/90 hover:scale-105"
          @click.prevent="props.setActivitiesPanel(false)"
        >
          <Lucide class="w-8 h-8 stroke-[1]" icon="X" />
        </a>
        <Slideover.Title class="px-6 py-5 flex items-center justify-between gap-2">
          <h2 class="text-base font-semibold">{{ t("header_feed.title") }}</h2>
          <div v-if="loading" class="animate-spin text-slate-400">
            <Lucide icon="Loader2" class="w-4 h-4" />
          </div>
        </Slideover.Title>
        <Slideover.Description class="p-0 flex flex-col h-full bg-white overflow-hidden">
          <div class="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <div v-if="items.length > 0" class="space-y-3">
              <button
                v-for="n in items"
                :key="n.slug"
                type="button"
                class="group w-full text-left flex gap-3 p-3 rounded-xl hover:bg-slate-50 transition-all border border-transparent hover:border-slate-100"
                @click="openItem(n)"
              >
                <div
                  class="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-slate-100 border border-slate-100"
                >
                  <img
                    v-if="n.cover_url"
                    :src="n.cover_url"
                    alt=""
                    class="w-full h-full object-cover"
                  />
                  <div
                    v-else
                    class="w-full h-full flex items-center justify-center text-primary/70"
                  >
                    <Lucide icon="Newspaper" class="w-5 h-5" />
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-2 mb-0.5">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {{ t("header_feed.news") }}
                    </span>
                    <span class="text-[10px] text-slate-400 italic shrink-0">
                      {{ formatWhen(n.published_at) }}
                    </span>
                  </div>
                  <div
                    class="text-sm font-semibold text-slate-900 truncate group-hover:text-primary transition-colors"
                  >
                    {{ titleOf(n) }}
                  </div>
                  <p
                    v-if="excerptOf(n)"
                    class="mt-0.5 text-xs text-slate-500 line-clamp-2"
                  >
                    {{ excerptOf(n) }}
                  </p>
                </div>
              </button>
            </div>

            <div
              v-else-if="!loading"
              class="flex flex-col items-center justify-center h-64 text-center px-6"
            >
              <div
                class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-300"
              >
                <Lucide icon="Newspaper" class="w-8 h-8" />
              </div>
              <p class="text-sm font-medium text-slate-600">
                {{ t("header_feed.empty_title") }}
              </p>
              <p class="text-xs text-slate-400 mt-1">
                {{ t("header_feed.empty_desc") }}
              </p>
            </div>
          </div>

          <div class="p-4 border-t border-slate-100">
            <button
              type="button"
              class="w-full text-sm font-medium text-primary hover:underline py-1"
              @click="openAll"
            >
              {{ t("header_feed.see_all") }}
            </button>
          </div>
        </Slideover.Description>
      </Slideover.Panel>
    </Slideover>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}
</style>
